import { ObservableItemIdentifier } from '../../api-contracts/other/ObservableItemIdentifier';
import { ObservableLevelsEnum } from '../enums';

/** Leaf nodes */
type ScreenNode = Readonly<{
  name: string;
  paths: readonly string[];
  type: 'screen' | 'list';
  level?: ObservableLevelsEnum;
  children?: Readonly<Record<string, ScreenNode | ParentNode>>;
}>;

/** Parent nodes (recursive). Use an interface so recursion is OK. */
interface ParentNode {
  readonly name: string;
  readonly paths: readonly string[]; // often []
  readonly type: 'parent';
  readonly children: Readonly<Record<string, ScreenNode | ParentNode>>;
}

/** Discriminated union */
type TScreen = ScreenNode | ParentNode;

/** Maps */
type THeading = Readonly<Record<string, TScreen>>;
type TProgressIndicators = Readonly<Record<string, THeading>>;

/* IMPORTANT: no `: TProgressIndicators` var annotation.
   Use `as const satisfies` to preserve literal keys and arrays. */
const progressIndicators = {
  Root: {
    InspectionDate: {
      name: 'Inspection Date',
      paths: [ObservableItemIdentifier.Identifiers.LockOn.TimeLockOn],
      type: 'screen',
    },
    MachinePhoto: {
      name: 'Machine Photo',
      paths: [
        ObservableItemIdentifier.Identifiers.CoverPhoto.Photo,
        ObservableItemIdentifier.Identifiers.CoverPhoto.Completed,
      ],
      type: 'screen',
    },
    Operation: {
      name: 'Operation',
      paths: [
        ObservableItemIdentifier.Identifiers.Operation.TotalMachineHours,
        ObservableItemIdentifier.Identifiers.Operation.PitCondition
          .TotalTonnage,
        ObservableItemIdentifier.Identifiers.Operation.PitCondition
          .CurrentDiggingConditions,
        ObservableItemIdentifier.Identifiers.Operation.PitCondition
          .DiggingConditionsSinceLastInspection,
        ObservableItemIdentifier.Identifiers.Operation.PitCondition
          .TotalBankCubicMeters,
        ObservableItemIdentifier.Identifiers.Operation.TotalPropelHours,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Left.Checked,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Left.Date,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Left.MachineHoursAtChange,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Right.Checked,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Right.Date,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriageChangeOut
          .Right.MachineHoursAtChange,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriagePropelHours
          .Left,
        ObservableItemIdentifier.Identifiers.Operation.UndercarriagePropelHours
          .Right,
        ObservableItemIdentifier.Identifiers.Operation.Completed,
      ],
      type: 'screen',
    },
    ExecutiveSummary: {
      name: 'Executive Summary',
      paths: [
        ObservableItemIdentifier.Identifiers.ExecutiveSummary.Comments,
        ObservableItemIdentifier.Identifiers.ExecutiveSummary.Completed,
      ],
      type: 'screen',
    },
    Undercarriage: {
      name: 'Undercarriage',
      paths: [],
      type: 'parent',
      children: {
        Side: {
          name: 'Side',
          type: 'parent',
          paths: [],
          children: {
            SideFrame: {
              name: 'Side Frame',
              type: 'screen',
              level: ObservableLevelsEnum.equipment,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.SideFrame
                  .Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.SideFrame
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.SideFrame
                  .Completed,
              ],
            },
            PitchExtension: {
              name: 'Pitch Extension',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Bushings.Cracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Bushings.Loss.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Bushings.Missing,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Bushings.Walking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Hardware.Damage.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Hardware.Loose.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Hardware.Missing.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.PinBoreLugs.AbnormalWear
                  .Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.PinBoreLugs.Broken.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.PinBoreLugs.Cracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.WearIndicators.Pins.Broken.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.PitchGauge.CalculatedPitchGauge,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.TapeMeasure.CalculatedTapePitchExtension,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .PitchExtension.Completed,
              ],
            },
            ShoeRollerPath: {
              name: 'Shoe Roller Path',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .RollerPath.WearIndicators.SevereCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .RollerPath.WearIndicators.SpallingDelaminationMinorCracking
                  .Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .RollerPath.WearIndicators.Toenailing.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Wear.Measurement,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.SevereCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.SpallingDelaminationMinorCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.Toenailing.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .RollerPath.Completed,
              ],
            },
            ShoeDriveLug: {
              name: 'Shoe Drive Lug',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .DriveLug.WearIndicators.SevereCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .DriveLug.WearIndicators.SpallingDelaminationMinorCracking
                  .Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .DriveLug.WearIndicators.PlasticDeformation.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .WornLength.Measurement,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Shoes
                  .DriveLug.Completed,
              ],
            },
            TumblerRollerPath: {
              name: 'Tumbler Roller Path',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Wear.Measurement,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.SevereCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.SpallingDelaminationMinorCracking.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .WearIndicators.Toenailing.Checked,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.RollerPath
                  .Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveTumbler
                  .RollerPath.Completed,
              ],
            },
            TumblerDriveLug: {
              name: 'Tumbler Drive Lug',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .WornLength.Measurement,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .WearIndicators.PlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .WearIndicators.SpallingDelaminationMinorCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.DriveTumbler
                  .DriveLug.Completed,
              ],
            },
            Engagement: {
              name: 'Engagement',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage
                  .TumblerEngagement.Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage
                  .TumblerEngagement.Photo,
              ],
            },
            FrontIdler: {
              name: 'Front Idler',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                // #10549: since the epic (#10249) FrontScreen writes Visual Condition
                // to the Front identifier (the R8 fix; item seeded 2026-08-23) — track
                // it here or the answer never registers in the ToC progress.
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Front
                  .VisualCondition,
                // #10330 legacy: pre-epic captures (and reopened inspections) stored
                // the FRONT condition under the Rear identifier; observations are
                // scoped to each idler part's entityExternalId, so this is side-safe.
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Rear
                  .VisualCondition,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.Collapsed,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ExcessAxleBoreClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ExcessiveGuideFaceClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.Leaking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.LooseOrMissingRetainingHardware,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators
                  .RollerPathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ThrustWasherDamagedOrMissing,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Front
                  .Completed,
              ],
            },
            RearIdler: {
              name: 'Rear Idler',
              type: 'screen',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Rear
                  .VisualCondition,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.Collapsed,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ExcessAxleBoreClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ExcessiveGuideFaceClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.Leaking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.LooseOrMissingRetainingHardware,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators
                  .RollerPathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .WearIndicators.ThrustWasherDamagedOrMissing,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage.Idler.Rear
                  .Completed,
              ],
            },
            LoadRollers: {
              name: 'Load Rollers',
              type: 'list',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .VisualCondition,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Collapsed,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.BuriedWithMedia,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.ExcessiveAxleBoreClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpot,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpotWear,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Leaking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.LooseMissingRetainingHardware,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.PedestalDamageWeldCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .RollerPathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SideframeDamage,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .SlidePathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage
                  .RollerAssemblies.LoadRoller.Completed,
              ],
            },
            ReturnRollers: {
              name: 'Return Rollers',
              type: 'list',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .VisualCondition,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Collapsed,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.BuriedWithMedia,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.ExcessiveAxleBoreClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpot,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpotWear,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Leaking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.LooseMissingRetainingHardware,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.PedestalDamageWeldCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .RollerPathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SideframeDamage,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .SlidePathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage
                  .RollerAssemblies.ReturnRoller.Completed,
              ],
            },
            Sliders: {
              name: 'Sliders',
              type: 'list',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .VisualCondition,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Collapsed,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.BuriedWithMedia,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.ExcessiveAxleBoreClearance,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpot,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.FlatSpotWear,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.Leaking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.LooseMissingRetainingHardware,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.PedestalDamageWeldCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .RollerPathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SevereCracking,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators.SideframeDamage,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .WearIndicators
                  .SlidePathCrackingDelaminationPlasticDeformation,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller
                  .Comments,
                ObservableItemIdentifier.Identifiers.Undercarriage.Roller.Photo,
                ObservableItemIdentifier.Identifiers.Undercarriage
                  .RollerAssemblies.Slider.Completed,
              ],
            },
          },
        },
      },
    },
    Bucket: {
      name: 'Bucket',
      paths: [],
      type: 'parent',
      children: {
        Summary: {
          name: 'Summary',
          type: 'screen',
          level: ObservableLevelsEnum.equipment,
          paths: [
            ObservableItemIdentifier.Identifiers.Bucket.Summary.Comments,
            ObservableItemIdentifier.Identifiers.Bucket.Summary.Photo,
            ObservableItemIdentifier.Identifiers.Bucket.Summary.Completed,
          ],
        },
        WearKit: {
          name: 'Wear Kit',
          type: 'parent',
          paths: [],
          children: {
            Summary: {
              name: 'Summary',
              type: 'screen',
              level: ObservableLevelsEnum.equipment,
              paths: [
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Summary.Comments,
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Summary.Photo,
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Summary.Completed,
              ],
            },
            HeelShrouds: {
              name: 'Heel Shrouds',
              type: 'list',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Measurement.Tool,
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Measurement.Position.Value,
              ],
            },
            WearPlates: {
              name: 'Wear Plates',
              type: 'list',
              level: ObservableLevelsEnum.equipmentPart,
              paths: [
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Measurement.Position.Value,
                ObservableItemIdentifier.Identifiers.Bucket.Condition.WearKit
                  .Measurement.Tool,
              ],
              children: {
                PrimaryLocation: {
                  name: 'Primary Location',
                  type: 'list',
                  level: ObservableLevelsEnum.equipment,
                  paths: [
                    ObservableItemIdentifier.Identifiers.Bucket.Condition
                      .WearKit.WearPlates.Summary.Comments,
                    ObservableItemIdentifier.Identifiers.Bucket.Condition
                      .WearKit.WearPlates.Summary.Photo,
                  ],
                  children: {
                    Summary: {
                      name: 'Summary',
                      type: 'screen',
                      level: ObservableLevelsEnum.equipment,
                      paths: [
                        ObservableItemIdentifier.Identifiers.Bucket.Condition
                          .WearKit.WearPlates.Location.Comments,
                        ObservableItemIdentifier.Identifiers.Bucket.Condition
                          .WearKit.WearPlates.Location.Photo,
                      ],
                    },
                  },
                },
              },
            },
          },
        },
        GET: {
          name: 'Ground Engaging Tools',
          type: 'parent',
          paths: [],
          children: {
            Summary: {
              name: 'Summary',
              type: 'screen',
              level: ObservableLevelsEnum.equipment,
              paths: [
                ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                  .Summary.Comments,
                ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                  .Summary.Photo,
                ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                  .Summary.Completed,
              ],
            },
            PartType: {
              name: 'Part Type',
              type: 'parent',
              paths: [],
              children: {
                Position: {
                  name: 'Position',
                  type: 'list',
                  level: ObservableLevelsEnum.equipmentPart,
                  paths: [
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Measurement.Length,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Measurement.Thickness,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Measurement.Tool,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Measurement.Value,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Base
                      .Damaged,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Base
                      .Missing,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Part
                      .Damaged,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Part
                      .Missing,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Pin.Damaged,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Pin
                      .LooseAndRetightened,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.WearIndicators.Pin.Missing,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Comments,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.Condition.Photo,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.PartHours,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Position.TotalBases,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Position.TotalParts,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Position.TotalPins,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Reason.Damaged.Checked,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Reason.Missing.Checked,
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Reason.Worn.Checked,
                    // SameParts.Checked and PartId are seeded when the position is opened, so they are not progress (#11628).
                    ObservableItemIdentifier.Identifiers.Bucket
                      .GroundEngagingTools.ChangeOut.Specification.NewParts
                      .Checked,
                  ],
                },
              },
            },
          },
        },
      },
    },
  },
} as const satisfies TProgressIndicators;

export type ProgressIndicators = typeof progressIndicators;
export type { THeading, TScreen };
export default progressIndicators;
